/* eslint-disable no-console */
import { log } from '@axa-fr-axion/log';
import { FunctionalErrorReason } from '@axa-fr-axion/restful';
import {
    Civilite,
    Conseiller,
    Dossier,
    Personne,
    Adresse,
    Propriete,
    OrigineData,
} from '@axa-fr-axion/user-journey-services';
import { OptionData } from '@axafr-btc-ds/form-select';
import { mapAnalyticToProprietes } from '@axa-fr-axion/analytic-cookie';
import { produce } from 'immer';
import merge from 'lodash.merge';
import {
    AppOptions,
    JourneyData,
    JourneyDataValidity,
    JourneyInitialData,
    Funnel,
} from './App.interface';
import { CompanyData } from 'features/company';
import {
    HomeAddressData,
    IdentityData,
} from 'features/contact';
import { GenderOptions } from 'features/contact/identity/gender';
import { Establishment, EstablishmentEffectiveSlice } from 'services/shared';
import { NavigationPath, StepNavigationPath } from 'interfaces';
import { EconomicSectorData } from 'features/economic-sector';
import { IncomeProps } from 'features/income/Income.interface';
import { LegalStatusProps } from 'features/legal-status/LegalStatus.interface';
import { getProductCodes } from './features/pre-choose-advisor/PreChooseAdvisor.utils';
import {
    YearsOfExperienceProps,
    YearsOfExperienceData,
} from 'features/years-of-experience';
import { CatalogMap } from 'features/catalog';
import { NumberOfEmployeesData } from 'features/number-of-employees';
import parse from 'date-fns/parse';
import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import addYears from 'date-fns/addYears';
import { Preset, PresetDefinition } from 'presets';
import { allProducts } from 'services/catalog/Catalog.data';
import { HasPeculiaritiesProps } from 'features/has-peculiarities';
import { CumulativeValueProps } from 'features/cumulative-value-of-goods/cumulative-value';
import { BusinessPremisesOwnershipProps } from 'features/business-premises-ownership';
import { BusinessPremisesPresenceProps } from 'features/business-premises-presence';
import { BusinessPremisesSurfaceProps } from 'features/business-premises-surface';
import { Product } from 'services/catalog';
import { HasEmployeesProps } from 'features/has-employees';

export const findAgentLink = 'https://www.findagent.com';
export const DEFAULT_OPTIONS: AppOptions = {
    editorialCommon: {
        axaPartnerPhoneNumber: '0970821010',
        findAdvisorWhenApartFromMetropolisLink: findAgentLink,
        findAdvisorWhenUnreliableCoordinatesLink: findAgentLink,
        findAdvisorWhenRejectedLink: findAgentLink,
    },
};

export const EMPTY_JOURNEY_VALIDITY_DATA: JourneyDataValidity = {
    isCatalogValid: false,
    isEconomicSectorValid: false,
    isIdentityValid: false,
    isHomeAddressValid: false,
    isLegalStatusValid: false,
    isNumberOfEmployeesValid: false,
    isIncomeValid: false,
    isYearsOfExperienceValid: false,
    isHasPeculiaritiesValid: false,
    isCumulativeValueOfGoodsValid: false,
    isBusinessPremisesPresenceValid: false,
    isBusinessPremisesSurfaceValid: false,
    isCompanyValid: false,
    isSelectedAdvisorValid: false,
    isRecommandationValid: false,
    isCatalogRecommandationValid: false,
    isBusinessPremisesOwnershipValid: false,
    isHasEmployeesValid: false,
    isTarificationValid: false,
    isProprietesValid: false,
    isRequestErrorValid: false,
};
const END_REJECT_PAGE: StepNavigationPath = 'End/Reject';
const HORS_METROPOLE_PAGE: StepNavigationPath = 'End/Apart-from-metropolis';
const CORDONNES_NON_FIABLE_PAGE: StepNavigationPath = 'End/Unreliable-contact-information';

export const EMPTY_JOURNEY_DATA: JourneyData = {
    ...EMPTY_JOURNEY_VALIDITY_DATA,
    catalog: {
        defaultProductCodes: undefined,
        selectedProducts: undefined,
    },
    catalogRecommandation: {
        selectedProducts: undefined,
        recommandationProducts: undefined,
    },
    company: {
        siretNumber: undefined,
    },
    economicSector: {
        sector: undefined,
        profession: undefined,
    },
    legalStatus: undefined,
    numberOfEmployees: {
        slice: undefined,
    },
    income: undefined,
    yearsOfExperience: undefined,
    folder: {
        id: undefined,
        lead: {
            id: undefined,
        },
    },
    identity: {
        gender: undefined,
        firstname: undefined,
        lastname: undefined,
        phone: undefined,
        email: undefined,
    },
    homeAddress: {
        address: undefined,
        companyName: undefined,
        postalCode: undefined,
        city: undefined,
    },
    cumulativeValueOfGoods: { cumulativeValue: undefined },
    tarification: undefined,
    proprietes: undefined,
};

export const ORIGIN_ITEM_KEY_FOR_PRODUCT = 'produits';

export const initJourneyDataWithOrigin = (
    origin?: OrigineData,
) => (
    initialData: Partial<JourneyInitialData> | undefined,
): JourneyData => {
    let initialDataFromOrigin: Partial<JourneyInitialData> = {};

    const preSelectedProductsFromOrigin = origin?.items?.find(item => item.nom === ORIGIN_ITEM_KEY_FOR_PRODUCT);
    if (!!preSelectedProductsFromOrigin?.valeur) {
        initialDataFromOrigin = {
            catalog: {
                defaultProductCodes: preSelectedProductsFromOrigin.valeur.split(','),
            },
        };
    }

    return merge(
        {},
        EMPTY_JOURNEY_DATA,
        initialData,
        initialDataFromOrigin,
    );
};

export const forceMRPProductForMicroEntPreset = (
    fnc: (data: Partial<JourneyInitialData> | undefined) => JourneyData,
    preset: Pick<PresetDefinition, 'type'>,
) => (
    initialData: Partial<JourneyInitialData> | undefined,
): JourneyData => {
    let initialDataForMicroEnt: Partial<JourneyInitialData> = {};

    if (preset.type === 'MicroEnt') {
        const MRPProduct: Product = {
            categorieProduit: 'IARD',
            codeProduit: 'PACKMICROENTREPRENEUR',
            enSavoirPlus: null,
            libelleProduit: 'Assurance multirisque professionnelle',
        };

        initialDataForMicroEnt = {
            catalog: {
                selectedProducts: new Map([
                    [ 'activite', new Set<Product>([ MRPProduct ]) ],
                ]),
            },
        };
    }

    return merge(
        fnc(initialData),
        initialDataForMicroEnt,
    );
};

export function getInitialStep(
    options: AppOptions,
    preset: PresetDefinition
): StepNavigationPath {
    if (options.config?.isRecommandationEnabled) {
        return 'RecommandationHome';
    }

    if (preset.screens?.catalog) {
        return 'Catalog';
    }

    return 'Company';
}

export const folderReady = (journeyData: JourneyData): boolean =>
    Boolean(journeyData.folder.id && journeyData.folder.lead?.id);

export const getSelectedProducts = (journeyData: JourneyData): CatalogMap | undefined => {
    const { recommandation, catalog, catalogRecommandation } = journeyData;

    if (recommandation?.value === 'advice') {
        return catalogRecommandation.selectedProducts;
    }

    return catalog.selectedProducts;
};

export const assignProprietes = (journeyData: JourneyData): Dossier => produce(journeyData.folder, (draft) => {
    draft.proprietes = [ ...(journeyData.folder.proprietes ?? []) ];

    switch (journeyData.recommandation?.value) {
        case 'advice':
            mapCatalogRecommandationDataToProprietes(
                draft,
                journeyData.catalogRecommandation.selectedProducts,
            );
            break;
        case 'manual':
        case undefined:
            mapCatalogDataToProprietes(
                draft,
                journeyData.catalog.selectedProducts,
            );
            break;
    }

    mapCompanyToProprietes(draft, journeyData.company);
    mapHomeAddressDataToProprietes(draft, journeyData.homeAddress);
    mapEconomicSectorToProprietes(draft, journeyData.economicSector);
    mapLegalStatusToProprietes(draft, journeyData.legalStatus);
    mapNumberOfEmployeesToProprietes(draft, journeyData.numberOfEmployees);
    mapIncomeToProprietes(draft, journeyData.income);
    mapYearsOfExperience(draft, journeyData.yearsOfExperience);
    mapProductCodesToProprietes(draft, getSelectedProducts(journeyData));
    mapHasPeculiaritiesToProprietes(draft, journeyData.hasPeculiarities);
    mapCumulativeValueOfGoodsToProprietes(draft, journeyData.cumulativeValueOfGoods?.cumulativeValue);
    mapBusinessPremisesPresenceToProprietes(draft, journeyData.businessPremisesPresence);
    mapBusinessPremisesOwnershipToProprietes(draft, journeyData.businessPremisesOwnership);
    mapBusinessPremisesSurfaceToProprietes(draft, journeyData.businessPremisesSurface);
    mapBusinessEmployeeToProprietes(draft, journeyData.hasEmployees);
});

export const assignProprietesForMicroEnt = (journeyData: JourneyData): Dossier => produce(
    journeyData.folder,
    (draft) => {
        const journeyProperties = Object.assign([], journeyData.proprietes);
        const origineItems = Object.assign([], journeyData.folder.proprietes);
        draft.proprietes = [ ...origineItems, ...journeyProperties ];
        return draft;
    }
);

export const assignProductCodesAndAnalyticsProprietes =
    (journeyData: JourneyData, preset?: Pick<PresetDefinition, 'type'>): Dossier => produce(journeyData.folder, (draft) => {
        draft.proprietes = [ ...(journeyData.folder.proprietes ?? []) ];
        mapAnalyticToProprietes(draft, journeyData.analytic);
        if (preset?.type !== 'MicroEnt') {
            mapProductCodesToProprietes(draft, getSelectedProducts(journeyData));
        }
    });

export const toCivilite = (gender?: GenderOptions): Civilite | undefined => {
    if (!gender) {
        return undefined;
    }
    switch (gender) {
        case 'ms':
            return 'Mme';
        case 'mr':
            return 'M';
        default:
            throw new Error(`Unknown gender [${ gender }]`);
    }
};

export const toGender = (civilite?: Civilite | string | null): GenderOptions | undefined => {
    switch (civilite) {
        case 'M':
            return 'mr';
        case 'Mme':
            return 'ms';
        default:
            return undefined;
    }
};

export const toAdvisor = (advisor: Conseiller) => {
    const city = [
        advisor.adresse?.codePostal,
        advisor.adresse?.ville,
    ].filter(Boolean).join(' ');
    const streets = [
        advisor.adresse?.adresse,
        advisor.adresse?.[ 'complement-adresse' ],
    ].filter(Boolean) as Array<string>;
    return {
        avatar: advisor.urlPhoto,
        firstName: advisor.prenom,
        lastName: advisor.nom,
        status: advisor.reseau,
        streets,
        city: city || undefined,
        phone: advisor.tel1 || advisor.tel2,
        mail: advisor.email,
        openingHours: advisor.openingHours?.at(0),
        website: advisor.url,
        orias: advisor.orias,
        labelCommercial: advisor.libelleCommercial,
    };
};

export const toPersonne = (identity: IdentityData, address: HomeAddressData, nombreDeSalaries?: string): Personne => ({
    nom: identity.lastname,
    prenom: identity.firstname,
    civilite: toCivilite(identity.gender),
    adresse: toAdresse(address),
    telephone: identity.phone,
    email: identity.email?.trim(),
    optinAxa: identity.isOptin,
    ...(!!identity.numAbo && { numAbo: identity.numAbo }),
    nombreDeSalaries,
});

export const toIdentity = (personne: Personne | undefined): IdentityData => ({
    lastname: personne?.nom,
    firstname: personne?.prenom,
    gender: toGender(personne?.civilite),
    phone: personne?.telephone,
    email: personne?.email,
    isOptin: personne?.optinAxa,
    ...(personne?.numAbo && { numAbo: personne.numAbo }),
});

const endSteps: Record<Funnel | FunctionalErrorReason | string, StepNavigationPath> = {
    'HORS-CIBLE-PRODUIT': 'End/Off-target-product',
    'HORS-CIBLE': END_REJECT_PAGE,
    'HORS-LIMITE': END_REJECT_PAGE,
    'HORS-METROPOLE': HORS_METROPOLE_PAGE,
    'FIABILITE': CORDONNES_NON_FIABLE_PAGE,
    'DETENTEUR': 'End/Off-target-product',
    'AXAPART': 'End/AxaPartner',
    'SOLLICITATION': END_REJECT_PAGE,
    'PERSONALISATION': 'End/Confirmation',
    'REJET-SEC': 'End/Rejet-sec',
    'ABORT': 'End/Abort',
};

export const chooseEndStep = (inCase?: Funnel | FunctionalErrorReason | string): StepNavigationPath => {
    const step = inCase ? endSteps[ inCase ] : '';
    if (!step) {
        log.error(`Unknown case ${ inCase }`);
        return 'Error';
    }
    return step;
};

export const fromEstablishment = (establishment: Establishment | undefined): HomeAddressData | undefined => {
    if (!establishment) {
        return undefined;
    }

    const { address, company_name } = establishment;
    const { city, row4, zip_code } = address;

    if (!zip_code || !city) {
        return undefined;
    }

    return {
        address: row4,
        companyName: company_name,
        city,
        postalCode: zip_code,
    };
};

// NOTE: To be used inside immer js as we mute the draft object inside this function
export const mapEconomicSectorDataKeyToSalesforceKey: Record<keyof EconomicSectorData, string> = {
    sector: 'secteur',
    profession: 'detailActivite',
};

export function mapEconomicSectorToProprietes(draft: Dossier, data: EconomicSectorData): Dossier {
    const buffer: Array<Propriete> = [];
    Object.entries(data).forEach(([ key, value ]) => {
        const name = mapEconomicSectorDataKeyToSalesforceKey[ key as keyof EconomicSectorData ];

        if (Boolean(name) && Boolean(value)) {
            buffer.push({
                nom: name,
                valeur: getValue(value),
            });
        }
    });

    if (Boolean(draft.proprietes) && Array.isArray(draft.proprietes)) {
        draft.proprietes.push(...buffer);
    }

    return draft;
}

export function mapCompanyToProprietes(folder: Dossier, data: CompanyData): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        const { establishment, siretNumber } = data;

        if (siretNumber) {
            folder.proprietes.push({
                nom: 'num_siret',
                valeur: siretNumber,
            });
        }

        if (establishment) {
            const {
                APET700_establishment_activity,
                establishment_activity_label,
                establishment_creation_date,
            } = establishment;

            if (establishment_creation_date) {
                // Adding a date library just for that is not worth it. But when we have more use cases with dates,
                // we can replace this code by a proper `parse('01012023', 'DDMMYYY').format('YYYY')` or similar.
                const startIndex = 4;
                const endIndex = 8;
                folder.proprietes.push({
                    nom: 'anneeCreation',
                    valeur: establishment_creation_date.substring(startIndex, endIndex),
                });
            }

            folder.proprietes.push({
                nom: 'activiteNAF',
                valeur: [
                    APET700_establishment_activity || '',
                    establishment_activity_label || '',
                ].join(' ').trim(),
            });
        }
    }

    return folder;
}

export function mapLegalStatusToProprietes(folder: Dossier, data: LegalStatusProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'statutJuridique',
            valeur: data,
        });
    }

    return folder;
}

export function mapNumberOfEmployeesToProprietes(folder: Dossier, data: NumberOfEmployeesData): Dossier {
    if (data.slice?.label?.length && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'Effectif',
            valeur: data.slice?.label,
        });
    }

    return folder;
}

export function mapIncomeToProprietes(folder: Dossier, data: IncomeProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'chiffreAffaires',
            valeur: data,
        });
    }

    return folder;
}

export function mapYearsOfExperience(folder: Dossier, data: YearsOfExperienceProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'nbAnneeExperience',
            valeur: data,
        });
    }

    return folder;
}

export function mapProductCodesToProprietes(folder: Dossier, data: CatalogMap = new Map()): Dossier {
    if (Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'codesProduit',
            valeur: getProductCodes(data).toString(),
        });
    }

    return folder;
}

export function getValue(data: string | OptionData): string {
    if (typeof data === 'string') {
        return data;
    }

    return data.label;
}

export function getNumberOfEmployeesFromEstablishment(
    establishment: Establishment | undefined
): Exclude<EstablishmentEffectiveSlice, 'NN'> | undefined {
    const value = establishment?.establishment_effective_slice;

    if (!value) {
        return undefined;
    }

    if (value === 'NN') {
        return '00';
    }

    return value;
}

export function getYearsOfExperienceFromEstablishment(
    establishment: Establishment | undefined,
    today = new Date(),
): YearsOfExperienceData | undefined {
    const establishmentCreationDate = establishment?.establishment_creation_date;
    const threshold = 3;

    if (!establishmentCreationDate) {
        return undefined;
    }

    const create = parse(establishmentCreationDate, 'ddMMyyyy', new Date());
    const dateToCompare = addYears(create, threshold);

    if (isAfter(today, dateToCompare) || isSameDay(today, dateToCompare)) {
        return '3 ans et plus';
    }
    return 'Moins de 3 ans';
}

export function mapHomeAddressDataToProprietes(folder: Dossier, data: HomeAddressData): Dossier {
    if (folder.proprietes) {
        folder.proprietes = [
            ...folder.proprietes,
            {
                nom: 'raison',
                valeur: data.companyName || '',
            },
        ];
    }

    return folder;
}

export const toAdresse = (address: HomeAddressData): Adresse => ({
    adresse: address.address,
    codePostal: address.postalCode,
    ville: address.city,
    pays: 'FRA',
});

export function mapCatalogDataToProprietes(folder: Dossier, data: CatalogMap = new Map()): Dossier {
    if (folder.proprietes) {
        const buffer: Array<Propriete> = [];
        data.forEach((products, groupCode) => {
            const productLabels = Array.from(products)
                .map((product) => product.libelleProduit);

            if (folder.proprietes) {
                buffer.push({
                    nom: groupCode,
                    valeur: productLabels.join(','),
                });
            }
        });

        folder.proprietes = [
            ...folder.proprietes,
            ...buffer,
        ];
    }

    return folder;
}

export function mapCatalogRecommandationDataToProprietes(folder: Dossier, data: CatalogMap = new Map()): Dossier {
    if (folder.proprietes) {
        const buffer: Array<Propriete> = [];
        const selectedProductCodes = Array.from(data.values())
            .map(productSet => Array.from(productSet.values()))
            .flat()
            .map(product => product.codeProduit);

        allProducts.groups.forEach(group => {
            const productLabels = group.products
                .filter(product => selectedProductCodes.includes(product.codeProduit))
                .map(product => product.libelleProduit);

            if (productLabels.length) {
                buffer.push({
                    nom: group.code,
                    valeur: productLabels.join(','),
                });
            }
        });

        folder.proprietes = [
            ...folder.proprietes,
            ...buffer,
        ];
    }

    return folder;
}

const hasManyAdvisors = (folder: Dossier): boolean => (folder.lead?.conseillers && folder.lead?.conseillers.length > 1)
    || false;
export const selectDefaultAdvisor = (folder: Dossier): Conseiller | undefined => {
    if (hasManyAdvisors(folder)) {
        return undefined;
    }
    return folder.lead?.conseillers?.at(0);
};

export const choosePostContactStep = (folder: Dossier, hasPricing = false): NavigationPath => {
    if (hasManyAdvisors(folder)) {
        return 'ChooseAdvisor';
    }

    if (hasPricing) {
        return 'PreTarification';
    }

    return 'PreConfirmation';
};

export const putAdvisorInLead = (folder: Dossier, advisor?: Conseiller): Dossier => ({
    ...folder,
    lead: {
        ...folder.lead,
        conseillers: advisor ? [ advisor ] : folder.lead?.conseillers,
    },
});

export function fromProductMapToProductCodes(data: CatalogMap = new Map()): ReadonlyArray<string> {
    return Array.from(data.values())
        .flatMap((products) => Array.from(products)
            .map((product) => product.codeProduit));
}

export function isCatalogVisibleFromOrigin(data?: OrigineData): boolean {
    return !data?.items?.find(item => item.nom === ORIGIN_ITEM_KEY_FOR_PRODUCT && !!item.valeur?.length);
}

export function mapHasPeculiaritiesToProprietes(folder: Dossier, data?: HasPeculiaritiesProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'specificiteEnt',
            valeur: data,
        });
    }

    return folder;
}

export function mapCumulativeValueOfGoodsToProprietes(folder: Dossier, data?: CumulativeValueProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'valeur_cumulee',
            valeur: data,
        });
    }

    return folder;
}

export function mapBusinessPremisesPresenceToProprietes(folder: Dossier, data?: BusinessPremisesPresenceProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'localProfessionnel',
            valeur: data,
        });
    }

    return folder;
}

export function mapBusinessPremisesOwnershipToProprietes(folder: Dossier, data?: BusinessPremisesOwnershipProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'statutOccupation',
            valeur: data,
        });
    }

    return folder;
}

export function mapBusinessPremisesSurfaceToProprietes(folder: Dossier, data?: BusinessPremisesSurfaceProps['data']): Dossier {
    if (data?.surface && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'surface',
            valeur: data.surface,
        });
    }

    return folder;
}

export function mapBusinessEmployeeToProprietes(folder: Dossier, data?: HasEmployeesProps['data']): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'Effectif',
            valeur: data,
        });
    }

    return folder;
}

export const handlePreLegalStatusBody = (preset: Preset, journeyData: JourneyData): Establishment | undefined => {
    if (preset === 'MicroEnt' && journeyData.company.establishment) {
        return journeyData.company.establishment;
    }

    return undefined;
};
